import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ANNOUNCEKIT_BOOSTER_BAR_SELECTOR } from 'src/app/initializers/announcekit-bar-height';
import { NotificationVisitService } from 'src/app/services/notification-visit.service';
import { TrackingService } from 'src/app/services/tracking/service';

/**
 * CalloutComponent is a reusable component that displays a callout (a small overlay)
 * with a title, description, and dismiss button.
 */
@Component({
  selector: 'app-callout',
  templateUrl: './callout.component.html',
  styleUrls: ['./callout.component.scss'],
})
export class CalloutComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() id = 'callout';
  @Input() title: string;
  @Input() description: string;
  @Input() dismissButtonLabel?: string;
  @Input() origin: any;
  @Input() position: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-center' = 'left';
  @Input() timeout: number;
  @Input() hidden = false;
  @Input() hideForSmallScreens = false;
  @Input() panelClass = '';
  @Input() saveVisitByDB?: boolean;
  @Input() isTracking?: boolean;
  @Input() ctaButtonLabel?: string;
  @Input() ctaButtonAction?: (injector: Injector) => void;
  @Input() showCtaInsteadOfDismiss?: boolean;
  @Input() dismissAction?: (injector: Injector) => void;
  @Input() enableScrollReposition = false;
  /** When true, repositions when page layout shifts (e.g. marketing banners). Academy callout only. */
  @Input() repositionOnLayoutChange = false;
  /** When true, keeps the overlay inside the viewport. Academy callout only. */
  @Input() pushToViewport = false;
  /** Override CDK connected positions (Academy callout only). */
  @Input() connectedPositions?: ConnectedPosition[];
  /** When false, prevents CDK from shrinking the overlay (Academy callout only). */
  @Input() flexibleDimensions = true;

  show = false;
  scrollStrategy: ScrollStrategy;

  @ViewChild(CdkConnectedOverlay) private connectedOverlay?: CdkConnectedOverlay;

  private layoutObserver?: ResizeObserver;
  private layoutMutationObserver?: MutationObserver;
  private readonly observedLayoutElements = new WeakSet<Element>();
  private scrollContainers: HTMLElement[] = [];
  private scrollFrame?: number;
  private readonly onScrollReposition = () => {
    if (this.scrollFrame != null) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      this.reapplyOverlayPosition();
    });
  };

  private get storageKey() {
    return `callout${this.id}`;
  }

  get dismissed() {
    if (!this.saveVisitByDB) {
      try {
        return localStorage.getItem(this.storageKey) === 'false';
      } catch (e) {
        console.error('Failed to access localStorage:', e);
        return false;
      }
    }
    const isVisitedCallout = this.notifVisitService.visitedNotification(this.storageKey);
    return isVisitedCallout;
  }

  get overlayOrigin() {
    return this.resolveOriginElement() ?? this.origin;
  }

  get overlayPositions(): ConnectedPosition[] {
    return this.connectedPositions ?? this.defaultPositions;
  }

  get defaultPositions(): ConnectedPosition[] {
    switch (this.position) {
      case 'top-right':
        return [
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8,
          },
        ];
      case 'top-center':
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
        ];
      case 'bottom':
        return [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8,
          },
        ];
      case 'left':
        return [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
            offsetX: -8,
          },
        ];
      case 'right':
        return [
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top',
            offsetX: 8,
          },
        ];
      case 'top':
        return [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ];
      default:
        return [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ];
    }
  }

  constructor(
    private cd: ChangeDetectorRef,
    private readonly notifVisitService: NotificationVisitService,
    private injector: Injector,
    private readonly trackingService: TrackingService,
    private overlay: Overlay,
  ) {
    // Initialize with noop strategy (does nothing on scroll) as the default
    this.scrollStrategy = this.overlay.scrollStrategies.noop();
  }

  ngOnInit() {
    if (this.dismissed) {
      return;
    }

    // Set up scroll strategy if enabled
    if (this.enableScrollReposition) {
      this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    }

    setTimeout(() => {
      this.show = true;
      this.cd.detectChanges();
      this.startLayoutObservation();
      requestAnimationFrame(() => {
        this.reapplyOverlayPosition();
        requestAnimationFrame(() => this.reapplyOverlayPosition());
      });
    }, this.timeout);
  }

  ngAfterViewInit() {
    if (this.show) {
      this.startLayoutObservation();
    }
  }

  ngOnDestroy() {
    this.layoutObserver?.disconnect();
    this.layoutMutationObserver?.disconnect();
    this.scrollContainers.forEach(container => {
      container.removeEventListener('scroll', this.onScrollReposition);
    });
    this.scrollContainers = [];
    if (this.scrollFrame != null) {
      cancelAnimationFrame(this.scrollFrame);
    }
  }

  /**
   * Recomputes overlay position against the current origin element (e.g. after top banners resize).
   * @returns true when the CDK position strategy was reapplied.
   */
  reapplyOverlayPosition(): boolean {
    const positionStrategy = (this.connectedOverlay?.overlayRef as unknown as {
      _positionStrategy?: { apply?: () => void, _isInitialRender?: boolean };
    })?._positionStrategy;
    if (positionStrategy?.apply) {
      positionStrategy._isInitialRender = true;
      positionStrategy.apply();
      return true;
    }

    this.connectedOverlay?.overlayRef?.updatePosition();
    return false;
  }

  private startLayoutObservation() {
    if (!this.repositionOnLayoutChange || this.layoutObserver) {
      return;
    }

    const originElement = this.resolveOriginElement();
    if (!originElement) {
      return;
    }

    const layoutRoot = originElement.closest('.page-container') ?? document.body;
    this.layoutObserver = new ResizeObserver(this.onScrollReposition);
    this.observeLayoutElementIfNeeded(originElement);
    this.observeLayoutElementIfNeeded(layoutRoot);
    this.observeExternalLayoutElements();

    const scrollRoots = [
      originElement.closest('mat-sidenav-content'),
      originElement.closest('.main-content-wrapper'),
      originElement.closest('.content'),
      layoutRoot,
    ].filter((element, index, elements): element is HTMLElement => {
      return element instanceof HTMLElement && elements.indexOf(element) === index;
    });

    scrollRoots.forEach(container => {
      container.addEventListener('scroll', this.onScrollReposition, { passive: true });
    });
    this.scrollContainers = scrollRoots;

    if (typeof MutationObserver !== 'undefined') {
      this.layoutMutationObserver = new MutationObserver(() => {
        this.observeExternalLayoutElements();
        this.onScrollReposition();
      });
      this.layoutMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      this.layoutMutationObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }
  }

  /** Watches banners injected outside `.page-container` (e.g. AnnounceKit booster bar). */
  private observeExternalLayoutElements(): void {
    if (!this.layoutObserver) {
      return;
    }

    document.querySelectorAll(ANNOUNCEKIT_BOOSTER_BAR_SELECTOR).forEach(element => {
      this.observeLayoutElementIfNeeded(element);
    });

    document.querySelectorAll('app-top-banner').forEach(topBannerHost => {
      this.observeLayoutElementIfNeeded(topBannerHost);
      topBannerHost.querySelectorAll('app-shared-banner').forEach(banner => {
        this.observeLayoutElementIfNeeded(banner);
      });
    });
  }

  private observeLayoutElementIfNeeded(element: Element): void {
    if (this.observedLayoutElements.has(element)) {
      return;
    }
    this.layoutObserver?.observe(element);
    this.observedLayoutElements.add(element);
  }

  private resolveOriginElement(): HTMLElement | null {
    if (!this.origin) {
      return null;
    }

    if (this.origin instanceof ElementRef) {
      return this.origin.nativeElement;
    }

    if (this.origin.elementRef instanceof ElementRef) {
      return this.origin.elementRef.nativeElement;
    }

    if (this.origin instanceof HTMLElement) {
      return this.origin;
    }

    if (this.origin?.nativeElement instanceof HTMLElement) {
      return this.origin.nativeElement;
    }

    return null;
  }

  dismissCallout() {
    // Track the dismiss button click with the actual button label
    if (this.isTracking && this.dismissButtonLabel) {
      this.trackingService.track('click', {
        page: `Clicked On ${this.dismissButtonLabel}`,
        location: 'Callout Notification',
      });
    }

    if(!this.saveVisitByDB) {
      try {
        localStorage.setItem(this.storageKey, 'false');
      } catch (e) {
        console.error('Failed to write to localStorage:', e);
      }
    } else {
      this.notifVisitService.setNotificationVisit(this.storageKey);
    }
    if (this.dismissAction) {
      this.dismissAction(this.injector);
    }
    this.show = false;
  }

  handleCtaClick() {
    // Track the CTA button click with the actual button label
    if (this.isTracking && this.ctaButtonLabel) {
      this.trackingService.track('click', {
        page: `Clicked On ${this.ctaButtonLabel}`,
        location: 'Callout Notification',
      });
    }

    if (this.ctaButtonAction) {
      this.ctaButtonAction(this.injector);
    }
  }
}
